import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCurrency } from './Contexts/CurrencyContext';
import {
  buildStaffPerformanceRows,
  getStaffPerformancePeriodMeta,
  STAFF_PERFORMANCE_PERIOD_OPTIONS
} from './lib/staffPerformance';

const STAFF_HOURS_PERIOD_OPTIONS = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' }
];

const DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT = '8';

const OWNER_ROLE_OPTIONS = [
  {
    id: 'service_team',
    label: 'Owner delivers services',
    description: 'Include the owner as service-delivery labour for pricing.'
  },
  {
    id: 'occasional',
    label: 'Owner works occasionally',
    description: 'Include only the owner hours you want pricing to account for.'
  },
  {
    id: 'non_service',
    label: 'Owner does not deliver services',
    description: 'Keep owner pay out of service labour cost calculations.'
  }
];

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDecimal = (value, decimals = 2) => (
  Number.isFinite(Number(value)) ? Number(value).toFixed(decimals) : '0.00'
);

const getPayMethodOptions = (isLimitedCompany) => (
  isLimitedCompany
    ? [
        { id: 'payroll', label: 'Payroll' },
        { id: 'dividends', label: 'Dividends' },
        { id: 'payroll_dividends', label: 'Payroll + dividends' },
        { id: 'not_planned', label: 'Not planning owner pay here' }
      ]
    : [
        { id: 'drawings', label: 'Owner drawings' },
        { id: 'not_planned', label: 'Not planning owner pay here' }
      ]
);

const createStaffMember = (index = 0, overrides = {}) => ({
  id: overrides.id || `staff-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
  name: overrides.name ?? '',
  hoursPeriod: STAFF_HOURS_PERIOD_OPTIONS.some((option) => option.id === overrides.hoursPeriod)
    ? overrides.hoursPeriod
    : 'weekly',
  hours: overrides.hours ?? '',
  hourlyPay: overrides.hourlyPay ?? '',
  employerCostPercent: overrides.employerCostPercent ?? '',
  holidayPayPercent: overrides.holidayPayPercent ?? ''
});

const normalizeStaffMembers = (
  staffMembers,
  targetCount = 0,
  defaultEmployerCostPercent = '',
  defaultHolidayPayPercent = DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT
) => {
  const requestedCount = Math.max(0, Number(targetCount) || 0);
  const sourceMembers = Array.isArray(staffMembers) ? staffMembers : [];
  const normalized = sourceMembers.map((member, index) => createStaffMember(index, {
    ...member,
    employerCostPercent: member?.employerCostPercent ?? defaultEmployerCostPercent,
    holidayPayPercent: member?.holidayPayPercent ?? defaultHolidayPayPercent
  }));

  while (normalized.length < requestedCount) {
    normalized.push(createStaffMember(normalized.length, {
      employerCostPercent: defaultEmployerCostPercent,
      holidayPayPercent: defaultHolidayPayPercent
    }));
  }

  return requestedCount > 0 ? normalized.slice(0, requestedCount) : normalized;
};

const getWeeklyHours = (period, hours, openDaysPerWeek) => {
  const numericHours = toNumber(hours);
  if (numericHours <= 0) return 0;

  if (period === 'daily') {
    return numericHours * Math.max(0, toNumber(openDaysPerWeek));
  }

  if (period === 'monthly') {
    return (numericHours * 12) / 52;
  }

  return numericHours;
};

const getLoadedHourlyCost = (hourlyPay, employerCostPercent, holidayPayPercent = 0) => {
  const base = toNumber(hourlyPay);
  const loading = Math.max(0, toNumber(employerCostPercent));
  const holidayPayReserve = Math.max(0, toNumber(holidayPayPercent));
  return base * (1 + (loading + holidayPayReserve) / 100);
};

const getWeeklyTargetFromAmount = (amount, period) => {
  const numAmount = amount === '' ? 0 : parseFloat(amount) || 0;
  const weeksPerYear = 52;
  const monthsPerYear = 12;

  switch (period) {
    case 'monthly':
      return (numAmount * monthsPerYear) / weeksPerYear;
    case 'yearly':
      return numAmount / weeksPerYear;
    case 'weekly':
    default:
      return numAmount;
  }
};

const getTargetsFromWeekly = (weekly) => ({
  weekly: weekly.toFixed(2),
  monthly: ((weekly * 52) / 12).toFixed(2),
  yearly: (weekly * 52).toFixed(2)
});

const getDefaultOwnerPayMethod = (isLimitedCompany) => (isLimitedCompany ? 'payroll' : 'drawings');

const buildGoalDraft = (targetIncomeData = {}, isLimitedCompany = false) => {
  const sourceStaffMembers = Array.isArray(targetIncomeData.staffMembers)
    ? targetIncomeData.staffMembers
    : [];
  const savedStaffCount = Math.max(0, Number(targetIncomeData.staffCount) || sourceStaffMembers.length || 0);
  const teamMode = targetIncomeData.teamMode === 'staff' || savedStaffCount > 0 ? 'staff' : 'solo';
  const staffCount = teamMode === 'staff' ? Math.max(1, savedStaffCount || 1) : 0;
  const ownerRole = ['service_team', 'occasional', 'non_service'].includes(targetIncomeData.ownerRole)
    ? targetIncomeData.ownerRole
    : (teamMode === 'staff' ? 'non_service' : 'service_team');
  const defaultEmployerCostPercent = targetIncomeData.defaultEmployerCostPercent ?? '';
  const defaultHolidayPayPercent = targetIncomeData.defaultHolidayPayPercent ?? DEFAULT_HOLIDAY_PAY_RESERVE_PERCENT;

  return {
    businessProfitGoalAmount: targetIncomeData.businessProfitGoalAmount ?? targetIncomeData.amount ?? '',
    businessProfitGoalPeriod: targetIncomeData.businessProfitGoalPeriod ?? targetIncomeData.period ?? 'weekly',
    openDaysPerWeek: targetIncomeData.openDaysPerWeek ?? targetIncomeData.workDaysPerWeek ?? 5,
    totalWeeklyOpenHours: targetIncomeData.totalWeeklyOpenHours ?? targetIncomeData.openHoursPerWeek ?? targetIncomeData.workHoursPerWeek ?? 30,
    ownerRole,
    ownerPayMethod: targetIncomeData.ownerPayMethod || getDefaultOwnerPayMethod(isLimitedCompany),
    ownerServiceHourlyRate: targetIncomeData.ownerServiceHourlyRate ?? targetIncomeData.goalHourly ?? '',
    ownerEmployerCostPercent: targetIncomeData.ownerEmployerCostPercent ?? '',
    ownerHoursPeriod: STAFF_HOURS_PERIOD_OPTIONS.some((option) => option.id === targetIncomeData.ownerHoursPeriod)
      ? targetIncomeData.ownerHoursPeriod
      : 'weekly',
    ownerHours: targetIncomeData.ownerHours ?? '',
    teamMode,
    defaultEmployerCostPercent,
    defaultHolidayPayPercent,
    staffCount,
    staffMembers: normalizeStaffMembers(sourceStaffMembers, staffCount, defaultEmployerCostPercent, defaultHolidayPayPercent)
  };
};

const BudgetSection = ({
  transactions,
  targetIncomeData,
  onGoalSaved,
  businessType = 'sole_trader',
  currentCountry = {}
}) => {
  const { formatCurrency } = useCurrency();
  const isLimitedCompany = businessType === 'limited_company';
  const isIreland = String(currentCountry?.id || '').toLowerCase() === 'ie' ||
    String(currentCountry?.name || '').toLowerCase().includes('ireland');
  const [goalDraft, setGoalDraft] = useState(() => buildGoalDraft(targetIncomeData, isLimitedCompany));
  const [savingGoal, setSavingGoal] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setGoalDraft(buildGoalDraft(targetIncomeData, isLimitedCompany));
  }, [targetIncomeData, isLimitedCompany]);

  const [targets, setTargets] = useState({
    weekly: '0.00',
    monthly: '0.00',
    yearly: '0.00'
  });

  const [budgetTracking, setBudgetTracking] = useState([
    { period: 'Week 1', target: 0, actual: 0 },
    { period: 'Week 2', target: 0, actual: 0 },
    { period: 'Week 3', target: 0, actual: 0 },
    { period: 'Week 4', target: 0, actual: 0 }
  ]);

  const [activeTab, setActiveTab] = useState('target');
  const [staffPerformancePeriod, setStaffPerformancePeriod] = useState('monthly');

  useEffect(() => {
    const savedBudgetTracking = localStorage.getItem('70livesbeautyledgerBudgetTracking');

    if (savedBudgetTracking) {
      try {
        setBudgetTracking(JSON.parse(savedBudgetTracking));
      } catch (e) {
        console.error('Failed to parse saved budget tracking data', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('70livesbeautyledgerBudgetTracking', JSON.stringify(budgetTracking));
  }, [budgetTracking]);

  const weeklyBusinessProfitTarget = useMemo(
    () => getWeeklyTargetFromAmount(goalDraft.businessProfitGoalAmount, goalDraft.businessProfitGoalPeriod),
    [goalDraft.businessProfitGoalAmount, goalDraft.businessProfitGoalPeriod]
  );

  useEffect(() => {
    setTargets(getTargetsFromWeekly(weeklyBusinessProfitTarget));
  }, [weeklyBusinessProfitTarget]);

  const calculateProgress = () => {
    const totalActual = budgetTracking.reduce((sum, week) => sum + week.actual, 0);
    const totalTarget = budgetTracking.reduce((sum, week) => sum + week.target, 0);
    const percentage = totalTarget === 0 ? 0 : (totalActual / totalTarget) * 100;

    return {
      actual: totalActual.toFixed(2),
      target: totalTarget.toFixed(2),
      percentage: Number.isFinite(percentage) ? percentage.toFixed(1) : '0.0'
    };
  };

  const progress = calculateProgress();

  const weeklyNetIncomeBuckets = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const buckets = [0, 0, 0, 0, 0];

    (transactions || []).forEach((t) => {
      if (!t.date) return;

      const date = new Date(t.date);
      if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) return;

      const weekIndex = Math.min(4, Math.floor((date.getDate() - 1) / 7));
      const amt = parseFloat(t.amount) || 0;
      const txType = (t.type || '').toLowerCase();
      if (txType === 'income') {
        buckets[weekIndex] += amt;
      } else if (txType === 'expense') {
        buckets[weekIndex] -= amt;
      }
    });

    return buckets;
  }, [transactions]);

  const openDaysPerWeek = toNumber(goalDraft.openDaysPerWeek);
  const weeklyOpenHours = toNumber(goalDraft.totalWeeklyOpenHours);
  const hoursPerOpenDay = openDaysPerWeek > 0 ? weeklyOpenHours / openDaysPerWeek : 0;
  const businessProfitHourlyTarget = weeklyOpenHours > 0 ? weeklyBusinessProfitTarget / weeklyOpenHours : 0;
  const ownerWorksInServices = goalDraft.ownerRole !== 'non_service';
  const hasStaff = goalDraft.teamMode === 'staff';

  const ownerWeeklyHours = ownerWorksInServices
    ? getWeeklyHours(goalDraft.ownerHoursPeriod, goalDraft.ownerHours, goalDraft.openDaysPerWeek)
    : 0;
  const ownerLoadedHourlyCost = ownerWorksInServices
    ? getLoadedHourlyCost(goalDraft.ownerServiceHourlyRate, goalDraft.ownerEmployerCostPercent)
    : 0;
  const ownerWeeklyLabourCost = ownerWeeklyHours * ownerLoadedHourlyCost;

  const labourPlan = useMemo(() => {
    const staffSummaries = hasStaff
      ? goalDraft.staffMembers.map((member, index) => {
          const weeklyHours = getWeeklyHours(member.hoursPeriod, member.hours, goalDraft.openDaysPerWeek);
          const baseHourlyPay = toNumber(member.hourlyPay);
          const employerCostPercent = toNumber(member.employerCostPercent);
          const holidayPayPercent = toNumber(member.holidayPayPercent);
          const baseWeeklyPay = weeklyHours * baseHourlyPay;
          const weeklyEmployerLoading = baseWeeklyPay * Math.max(0, employerCostPercent) / 100;
          const weeklyHolidayPayReserve = baseWeeklyPay * Math.max(0, holidayPayPercent) / 100;
          const loadedHourlyCost = getLoadedHourlyCost(member.hourlyPay, member.employerCostPercent, member.holidayPayPercent);
          const weeklyCost = weeklyHours * loadedHourlyCost;

          return {
            ...member,
            id: member.id || `staff-${index + 1}`,
            type: 'staff',
            displayName: member.name?.trim() || `Staff ${index + 1}`,
            weeklyHours,
            baseHourlyPay,
            employerCostPercent,
            holidayPayPercent,
            baseWeeklyPay,
            weeklyEmployerLoading,
            weeklyHolidayPayReserve,
            loadedHourlyCost,
            weeklyCost
          };
        })
      : [];

    const profiles = [];
    if (ownerWorksInServices && ownerLoadedHourlyCost > 0) {
      profiles.push({
        id: 'owner',
        type: 'owner',
        displayName: 'Owner',
        weeklyHours: ownerWeeklyHours,
        baseHourlyPay: toNumber(goalDraft.ownerServiceHourlyRate),
        employerCostPercent: toNumber(goalDraft.ownerEmployerCostPercent),
        holidayPayPercent: 0,
        baseWeeklyPay: ownerWeeklyHours * toNumber(goalDraft.ownerServiceHourlyRate),
        weeklyEmployerLoading: ownerWeeklyHours * toNumber(goalDraft.ownerServiceHourlyRate) * Math.max(0, toNumber(goalDraft.ownerEmployerCostPercent)) / 100,
        weeklyHolidayPayReserve: 0,
        loadedHourlyCost: ownerLoadedHourlyCost,
        weeklyCost: ownerWeeklyLabourCost
      });
    }

    staffSummaries.forEach((member) => {
      if (member.loadedHourlyCost > 0) {
        profiles.push(member);
      }
    });

    const totalWeeklyHours = profiles.reduce((sum, member) => sum + member.weeklyHours, 0);
    const totalWeeklyLabourCost = profiles.reduce((sum, member) => sum + member.weeklyCost, 0);
    const totalWeeklyHolidayPayReserve = staffSummaries.reduce((sum, member) => sum + member.weeklyHolidayPayReserve, 0);
    const equalShare = profiles.length > 0 ? 1 / profiles.length : 0;
    const weightedLoadedHourlyCost = totalWeeklyHours > 0
      ? profiles.reduce((sum, member) => sum + (member.loadedHourlyCost * member.weeklyHours), 0) / totalWeeklyHours
      : (
          profiles.length > 0
            ? profiles.reduce((sum, member) => sum + member.loadedHourlyCost, 0) / profiles.length
            : 0
        );

    const summaries = profiles.map((member) => {
      const share = totalWeeklyHours > 0 ? member.weeklyHours / totalWeeklyHours : equalShare;
      const weeklyProfitTarget = weeklyBusinessProfitTarget * share;
      const weeklyIncomeToGenerate = member.weeklyCost + weeklyProfitTarget;

      return {
        ...member,
        weeklyProfitTarget,
        monthlyProfitTarget: (weeklyProfitTarget * 52) / 12,
        yearlyProfitTarget: weeklyProfitTarget * 52,
        weeklyIncomeToGenerate,
        monthlyIncomeToGenerate: (weeklyIncomeToGenerate * 52) / 12,
        yearlyIncomeToGenerate: weeklyIncomeToGenerate * 52,
        monthlyCost: (member.weeklyCost * 52) / 12,
        yearlyCost: member.weeklyCost * 52
      };
    });

    return {
      staffSummaries,
      profiles,
      summaries,
      totalWeeklyHours,
      totalWeeklyLabourCost,
      totalMonthlyLabourCost: (totalWeeklyLabourCost * 52) / 12,
      totalYearlyLabourCost: totalWeeklyLabourCost * 52,
      totalWeeklyHolidayPayReserve,
      totalMonthlyHolidayPayReserve: (totalWeeklyHolidayPayReserve * 52) / 12,
      totalYearlyHolidayPayReserve: totalWeeklyHolidayPayReserve * 52,
      totalWeeklyIncomeNeeded: summaries.reduce((sum, member) => sum + member.weeklyIncomeToGenerate, 0),
      weightedLoadedHourlyCost
    };
  }, [
    goalDraft.openDaysPerWeek,
    goalDraft.ownerEmployerCostPercent,
    goalDraft.ownerServiceHourlyRate,
    goalDraft.staffMembers,
    hasStaff,
    ownerLoadedHourlyCost,
    ownerWeeklyHours,
    ownerWeeklyLabourCost,
    ownerWorksInServices,
    weeklyBusinessProfitTarget
  ]);
  const staffPerformanceMeta = useMemo(
    () => getStaffPerformancePeriodMeta(staffPerformancePeriod),
    [staffPerformancePeriod]
  );
  const staffPerformanceProfiles = useMemo(
    () => labourPlan.summaries.filter((member) => member.type === 'staff'),
    [labourPlan.summaries]
  );
  const staffPerformanceRows = useMemo(() => buildStaffPerformanceRows({
    transactions,
    staffProfiles: staffPerformanceProfiles,
    period: staffPerformancePeriod
  }), [staffPerformancePeriod, staffPerformanceProfiles, transactions]);
  const topRevenueStaffMember = useMemo(() => {
    const rankedByRevenue = staffPerformanceRows
      .filter((row) => row.saleCount > 0 || row.revenue > 0)
      .sort((left, right) => right.revenue - left.revenue);
    return rankedByRevenue[0] || null;
  }, [staffPerformanceRows]);
  const topContributionStaffMember = useMemo(() => {
    const rankedByContribution = staffPerformanceRows.filter(
      (row) => row.contributionAfterLabour !== null && (row.saleCount > 0 || row.revenue > 0)
    );
    return rankedByContribution[0] || null;
  }, [staffPerformanceRows]);
  const assignedStaffSalesCount = useMemo(
    () => staffPerformanceRows.reduce((sum, row) => sum + row.saleCount, 0),
    [staffPerformanceRows]
  );

  const pricingLaborHourlyCost = labourPlan.weightedLoadedHourlyCost || (ownerWorksInServices ? ownerLoadedHourlyCost : 0);
  const ownerPayMethodLabel = getPayMethodOptions(isLimitedCompany)
    .find((option) => option.id === goalDraft.ownerPayMethod)?.label || goalDraft.ownerPayMethod;

  const validateBudgetPlan = () => {
    if (!goalDraft.businessProfitGoalAmount || parseFloat(goalDraft.businessProfitGoalAmount) < 0) {
      return 'Please enter a valid business profit goal amount. Use 0 if you only want to plan labour costs for now.';
    }

    if (!goalDraft.businessProfitGoalPeriod) {
      return 'Please select a time period for your business profit goal.';
    }

    const days = toNumber(goalDraft.openDaysPerWeek);
    const hours = toNumber(goalDraft.totalWeeklyOpenHours);
    if (!days || days <= 0 || days > 7) {
      return 'Please enter open days per week between 1 and 7.';
    }
    if (!hours || hours <= 0) {
      return 'Please enter total weekly open hours.';
    }

    if (ownerWorksInServices && toNumber(goalDraft.ownerServiceHourlyRate) <= 0) {
      return 'Please enter the owner service hourly target, or set the owner role to not delivering services.';
    }

    if (hasStaff) {
      const invalidStaffMember = goalDraft.staffMembers.find((member) => (
        toNumber(member.hours) <= 0 || member.hourlyPay === '' || toNumber(member.hourlyPay) < 0
      ));
      if (invalidStaffMember) {
        return 'Please enter valid hours and hourly pay for each staff member.';
      }

      const invalidHolidayReserve = goalDraft.staffMembers.find((member) => toNumber(member.holidayPayPercent) < 0);
      if (invalidHolidayReserve || toNumber(goalDraft.defaultHolidayPayPercent) < 0) {
        return 'Please enter a holiday pay reserve percentage of 0 or more.';
      }
    }

    return '';
  };

  const handleSaveTargetIncome = () => {
    setSavingGoal(true);
    setSaveMessage({ type: '', text: '' });

    const validationError = validateBudgetPlan();
    if (validationError) {
      setSaveMessage({ type: 'error', text: validationError });
      setSavingGoal(false);
      return;
    }

    try {
      const normalizedStaffMembers = hasStaff
        ? goalDraft.staffMembers.map((member, index) => ({
            id: member.id || `staff-${index + 1}`,
            name: member.name?.trim() || `Staff ${index + 1}`,
            hoursPeriod: member.hoursPeriod || 'weekly',
            hours: member.hours ?? '',
            hourlyPay: member.hourlyPay ?? '',
            employerCostPercent: member.employerCostPercent ?? goalDraft.defaultEmployerCostPercent ?? '',
            holidayPayPercent: member.holidayPayPercent ?? goalDraft.defaultHolidayPayPercent ?? ''
          }))
        : [];
      const labourProfiles = labourPlan.profiles.map((profile) => ({
        id: profile.id,
        type: profile.type,
        name: profile.displayName,
        weeklyHours: Number(formatDecimal(profile.weeklyHours, 2)),
        baseHourlyPay: Number(formatDecimal(profile.baseHourlyPay, 2)),
        employerCostPercent: Number(formatDecimal(profile.employerCostPercent, 2)),
        holidayPayPercent: Number(formatDecimal(profile.holidayPayPercent, 2)),
        weeklyHolidayPayReserve: Number(formatDecimal(profile.weeklyHolidayPayReserve, 2)),
        loadedHourlyCost: Number(formatDecimal(profile.loadedHourlyCost, 2)),
        weeklyCost: Number(formatDecimal(profile.weeklyCost, 2))
      }));
      const payload = {
        amount: goalDraft.businessProfitGoalAmount,
        period: goalDraft.businessProfitGoalPeriod,
        businessProfitGoalAmount: goalDraft.businessProfitGoalAmount,
        businessProfitGoalPeriod: goalDraft.businessProfitGoalPeriod,
        openDaysPerWeek: goalDraft.openDaysPerWeek,
        totalWeeklyOpenHours: goalDraft.totalWeeklyOpenHours,
        workDaysPerWeek: goalDraft.openDaysPerWeek,
        workHoursPerWeek: goalDraft.totalWeeklyOpenHours,
        goalHourly: pricingLaborHourlyCost,
        businessProfitHourlyTarget,
        ownerRole: goalDraft.ownerRole,
        ownerPayMethod: goalDraft.ownerPayMethod,
        ownerServiceHourlyRate: goalDraft.ownerServiceHourlyRate,
        ownerEmployerCostPercent: goalDraft.ownerEmployerCostPercent,
        ownerHoursPeriod: goalDraft.ownerHoursPeriod,
        ownerHours: goalDraft.ownerHours,
        teamMode: hasStaff ? 'staff' : 'solo',
        defaultEmployerCostPercent: goalDraft.defaultEmployerCostPercent,
        defaultHolidayPayPercent: goalDraft.defaultHolidayPayPercent,
        staffCount: normalizedStaffMembers.length,
        staffMembers: normalizedStaffMembers,
        labourProfiles,
        pricingLaborHourlyCost,
        desiredProfitPerStaffMember: labourPlan.summaries.length
          ? weeklyBusinessProfitTarget / labourPlan.summaries.length
          : weeklyBusinessProfitTarget,
        staffPlanSummary: {
          totalWeeklyLabourHours: labourPlan.totalWeeklyHours,
          totalWeeklyLabourCost: labourPlan.totalWeeklyLabourCost,
          totalMonthlyLabourCost: labourPlan.totalMonthlyLabourCost,
          totalYearlyLabourCost: labourPlan.totalYearlyLabourCost,
          totalWeeklyHolidayPayReserve: labourPlan.totalWeeklyHolidayPayReserve,
          totalMonthlyHolidayPayReserve: labourPlan.totalMonthlyHolidayPayReserve,
          totalYearlyHolidayPayReserve: labourPlan.totalYearlyHolidayPayReserve,
          totalWeeklyIncomeNeeded: labourPlan.totalWeeklyIncomeNeeded,
          weightedLoadedHourlyCost: labourPlan.weightedLoadedHourlyCost,
          ownerPayMethodLabel
        }
      };

      localStorage.setItem('70livesbeautyledgerTargetIncome', JSON.stringify(payload));

      if (onGoalSaved) {
        onGoalSaved(payload);
      }

      setSaveMessage({ type: 'success', text: 'Budget plan saved.' });
    } catch (error) {
      console.error('Failed to save budget plan:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save budget plan. Please try again.' });
    } finally {
      setSavingGoal(false);
    }
  };

  const updateBudgetTracking = useCallback(() => {
    const weeklyTarget = parseFloat(targets.weekly);

    setBudgetTracking([
      { period: 'Week 1', target: weeklyTarget, actual: weeklyNetIncomeBuckets[0] },
      { period: 'Week 2', target: weeklyTarget, actual: weeklyNetIncomeBuckets[1] },
      { period: 'Week 3', target: weeklyTarget, actual: weeklyNetIncomeBuckets[2] },
      { period: 'Week 4', target: weeklyTarget, actual: weeklyNetIncomeBuckets[3] }
    ]);
  }, [targets.weekly, weeklyNetIncomeBuckets]);

  useEffect(() => {
    updateBudgetTracking();
  }, [updateBudgetTracking]);

  const profitGoalExplainer = isLimitedCompany
    ? 'This is the profit you want the company to keep after labour, products, overheads, and corporation tax planning. It is not sales income and it is not owner payroll.'
    : 'This is the surplus you want after labour, products, overheads, tax planning, and owner drawings. It is not total sales income.';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Financial Planning</h2>
        </div>

        <div className="mb-6">
          <div className="grid grid-cols-2 bg-primary-gradient p-1 rounded-xl shadow-sm">
            <button
              onClick={() => setActiveTab('target')}
              className={`py-3 px-5 rounded-lg transition ${
                activeTab === 'target'
                  ? 'bg-white text-[color:var(--color-text)] shadow-md' : 'text-[color:var(--color-text)] hover:bg-white/50'
              }`}
            >
              Budget Plan
            </button>

            <button
              onClick={() => setActiveTab('progress')}
              className={`py-3 px-5 rounded-lg transition ${
                activeTab === 'progress'
                  ? 'bg-white text-[color:var(--color-text)] shadow-md' : 'text-[color:var(--color-text)] hover:bg-white/50'
              }`}
            >
              Profit Progress
            </button>
          </div>
        </div>

        {activeTab === 'target' && (
          <div className="border-0 shadow-lg rounded-xl overflow-hidden">
            <div className="bg-[color:var(--color-accent)] text-[color:var(--color-text)] p-6">
              <h3 className="text-2xl">Budget Planner</h3>
              <p className="opacity-90">Separate owner pay, labour cost, and business profit before pricing services.</p>
            </div>
            <div className="p-6 bg-white space-y-6">
              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-4">
                  <h4 className="text-base font-semibold text-[color:var(--color-text)]">Business Profit Goal</h4>
                  <p className="text-xs text-gray-600 mt-1">{profitGoalExplainer}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-[color:var(--color-text)]">Business profit goal</label>
                    <input
                      type="text"
                      name="businessProfitGoalAmount"
                      value={goalDraft.businessProfitGoalAmount}
                      onChange={(e) => setGoalDraft((prev) => ({ ...prev, businessProfitGoalAmount: e.target.value }))}
                      className="w-full p-3 border rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[color:var(--color-text)]">Goal period</label>
                    <select
                      name="businessProfitGoalPeriod"
                      value={goalDraft.businessProfitGoalPeriod}
                      onChange={(e) => setGoalDraft((prev) => ({ ...prev, businessProfitGoalPeriod: e.target.value }))}
                      className="w-full p-3 border rounded-lg"
                    >
                      <option value="weekly">Every Week</option>
                      <option value="monthly">Every Month</option>
                      <option value="yearly">Every Year</option>
                    </select>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-4">
                  <h4 className="text-base font-semibold text-[color:var(--color-text)]">Opening Capacity</h4>
                  <p className="text-xs text-gray-600 mt-1">This is business availability, not necessarily owner working time.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-[color:var(--color-text)]">Open days per week</label>
                    <input
                      type="number"
                      min="1"
                      max="7"
                      value={goalDraft.openDaysPerWeek}
                      onChange={(e) => setGoalDraft((prev) => ({ ...prev, openDaysPerWeek: e.target.value }))}
                      className="w-full p-3 border rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[color:var(--color-text)]">Total weekly open hours</label>
                    <input
                      type="number"
                      min="1"
                      value={goalDraft.totalWeeklyOpenHours}
                      onChange={(e) => setGoalDraft((prev) => ({ ...prev, totalWeeklyOpenHours: e.target.value }))}
                      className="w-full p-3 border rounded-lg"
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-4">
                  <h4 className="text-base font-semibold text-[color:var(--color-text)]">Owner Pay Planning</h4>
                  <p className="text-xs text-gray-600 mt-1">
                    Owner payroll, dividends, or drawings are separate from business profit. Owner labour only affects pricing when the owner delivers services.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                  {OWNER_ROLE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setGoalDraft((prev) => ({ ...prev, ownerRole: option.id }))}
                      className={`rounded-lg border p-3 text-left transition ${
                        goalDraft.ownerRole === option.id
                          ? 'border-[color:var(--color-primary)] bg-white shadow-sm'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-semibold text-sm text-[color:var(--color-text)]">{option.label}</div>
                      <div className="mt-1 text-xs text-gray-600">{option.description}</div>
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-[color:var(--color-text)]">Owner pay method</label>
                    <select
                      value={goalDraft.ownerPayMethod}
                      onChange={(e) => setGoalDraft((prev) => ({ ...prev, ownerPayMethod: e.target.value }))}
                      className="w-full p-3 border rounded-lg bg-white"
                    >
                      {getPayMethodOptions(isLimitedCompany).map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
                    <div className="font-semibold text-[color:var(--color-text)]">How this affects pricing</div>
                    <div className="mt-1">
                      {ownerWorksInServices
                        ? 'Owner service hours and owner hourly target are included in the labour cost sent to the Pricing Calculator.'
                        : 'Owner pay is kept out of service labour cost because the owner is not delivering services.'}
                    </div>
                  </div>
                </div>

                {ownerWorksInServices && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Owner hours period</label>
                      <select
                        value={goalDraft.ownerHoursPeriod}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, ownerHoursPeriod: e.target.value }))}
                        className="w-full p-2.5 border rounded-lg bg-white"
                      >
                        {STAFF_HOURS_PERIOD_OPTIONS.map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Owner service hours</label>
                      <input
                        type="number"
                        min="0"
                        value={goalDraft.ownerHours}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, ownerHours: e.target.value }))}
                        className="w-full p-2.5 border rounded-lg"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Owner service hourly target</label>
                      <input
                        type="number"
                        min="0"
                        value={goalDraft.ownerServiceHourlyRate}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, ownerServiceHourlyRate: e.target.value }))}
                        className="w-full p-2.5 border rounded-lg"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Employer tax/loading %</label>
                      <input
                        type="number"
                        min="0"
                        value={goalDraft.ownerEmployerCostPercent}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, ownerEmployerCostPercent: e.target.value }))}
                        className="w-full p-2.5 border rounded-lg"
                      />
                      <p className="text-[11px] text-gray-500">
                        {isLimitedCompany && isIreland
                          ? 'Irish proprietary directors are often Class S, so employer PRSI is usually 0% unless the director is treated as an employee.'
                          : 'Only add employer loading where the owner pay method creates an employer cost.'}
                      </p>
                    </div>
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h4 className="text-base font-semibold text-[color:var(--color-text)]">Service Labour Team</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Staff pay is managed in the Staff tab. Budget uses that saved staff plan for labour cost, pricing, and holiday pay reserve calculations.
                    </p>
                  </div>
                </div>

                {hasStaff && (
                  <div className="mt-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-xs text-gray-500">Staff members</p>
                        <p className="text-lg font-bold">{labourPlan.staffSummaries.length}</p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-xs text-gray-500">Weekly staff labour</p>
                        <p className="text-lg font-bold">{formatCurrency(labourPlan.staffSummaries.reduce((sum, member) => sum + member.weeklyCost, 0))}</p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-xs text-gray-500">Holiday reserve weekly</p>
                        <p className="text-lg font-bold">{formatCurrency(labourPlan.totalWeeklyHolidayPayReserve)}</p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-xs text-gray-500">Holiday reserve monthly</p>
                        <p className="text-lg font-bold">{formatCurrency(labourPlan.totalMonthlyHolidayPayReserve)}</p>
                      </div>
                    </div>
                  </div>
                )}
                {!hasStaff && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-600">
                    No staff plan is saved yet. Add employees in the Staff tab when you need employee wage and holiday pay planning.
                  </div>
                )}
              </section>

              <section className="bg-primary-gradient rounded-xl shadow-md p-6">
                <h4 className="text-sm font-medium mb-4">Budget summary:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center sm:text-left p-2">
                    <h5 className="text-sm font-semibold text-[color:var(--color-text)]">Weekly profit goal</h5>
                    <p className="text-lg md:text-xl font-bold overflow-hidden text-ellipsis">{formatCurrency(targets.weekly)}</p>
                  </div>
                  <div className="text-center sm:text-left p-2">
                    <h5 className="text-sm font-semibold text-[color:var(--color-text)]">Monthly profit goal</h5>
                    <p className="text-lg md:text-xl font-bold overflow-hidden text-ellipsis">{formatCurrency(targets.monthly)}</p>
                  </div>
                  <div className="text-center sm:text-left p-2">
                    <h5 className="text-sm font-semibold text-[color:var(--color-text)]">Yearly profit goal</h5>
                    <p className="text-lg md:text-xl font-bold overflow-hidden text-ellipsis">{formatCurrency(targets.yearly)}</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div className="text-center sm:text-left p-2">
                    <h5 className="font-semibold text-[color:var(--color-text)]">Business profit per open hour</h5>
                    <p className="text-lg font-bold">{businessProfitHourlyTarget > 0 ? formatCurrency(businessProfitHourlyTarget) : '-'}</p>
                  </div>
                  <div className="text-center sm:text-left p-2">
                    <h5 className="font-semibold text-[color:var(--color-text)]">Loaded labour cost per hour</h5>
                    <p className="text-lg font-bold">{pricingLaborHourlyCost > 0 ? formatCurrency(pricingLaborHourlyCost) : '-'}</p>
                  </div>
                  <div className="text-center sm:text-left p-2">
                    <h5 className="font-semibold text-[color:var(--color-text)]">Open hours per week</h5>
                    <p className="text-lg font-bold">{weeklyOpenHours || '-'}</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div className="text-center sm:text-left p-2">
                    <h5 className="font-semibold text-[color:var(--color-text)]">Approx. hours per open day</h5>
                    <p className="text-lg font-bold">{hoursPerOpenDay > 0 ? hoursPerOpenDay.toFixed(1) : '-'}</p>
                  </div>
                  <div className="text-center sm:text-left p-2">
                    <h5 className="font-semibold text-[color:var(--color-text)]">Weekly labour cost</h5>
                    <p className="text-lg font-bold">{formatCurrency(labourPlan.totalWeeklyLabourCost)}</p>
                  </div>
                  <div className="text-center sm:text-left p-2">
                    <h5 className="font-semibold text-[color:var(--color-text)]">Pricing Calculator labour input</h5>
                    <p className="text-lg font-bold">{pricingLaborHourlyCost > 0 ? formatCurrency(pricingLaborHourlyCost) : '-'}</p>
                  </div>
                </div>
                {hasStaff && (
                  <div className="mt-4 rounded-lg border border-white/40 bg-white/70 p-3 text-sm">
                    <div className="font-semibold text-[color:var(--color-text)]">Employee holiday pay reserve</div>
                    <div className="mt-1 text-[color:var(--color-text)]">
                      Set aside {formatCurrency(labourPlan.totalWeeklyHolidayPayReserve)} weekly, {formatCurrency(labourPlan.totalMonthlyHolidayPayReserve)} monthly, or {formatCurrency(labourPlan.totalYearlyHolidayPayReserve)} yearly based on current staff wages.
                    </div>
                  </div>
                )}
              </section>

              {labourPlan.summaries.length > 0 && (
                <section className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h4 className="font-semibold text-[color:var(--color-text)]">Service Labour Breakdown</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Weekly income needed here covers labour cost plus allocated business profit only. Products, overheads, and VAT are added in Pricing Calculator.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white text-left text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Provider</th>
                          <th className="px-4 py-3">Weekly hours</th>
                          <th className="px-4 py-3">Base hourly</th>
                          <th className="px-4 py-3">Loading</th>
                          <th className="px-4 py-3">Holiday reserve</th>
                          <th className="px-4 py-3">Loaded hourly</th>
                          <th className="px-4 py-3">Weekly labour cost</th>
                          <th className="px-4 py-3">Weekly profit allocation</th>
                          <th className="px-4 py-3">Weekly income before overhead/products</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {labourPlan.summaries.map((provider) => (
                          <tr key={provider.id}>
                            <td className="px-4 py-3 font-semibold text-[color:var(--color-text)]">{provider.displayName}</td>
                            <td className="px-4 py-3">{provider.weeklyHours.toFixed(1)}</td>
                            <td className="px-4 py-3">{formatCurrency(provider.baseHourlyPay)}</td>
                            <td className="px-4 py-3">{provider.employerCostPercent.toFixed(1)}%</td>
                            <td className="px-4 py-3">
                              {provider.weeklyHolidayPayReserve > 0
                                ? `${formatCurrency(provider.weeklyHolidayPayReserve)} (${provider.holidayPayPercent.toFixed(1)}%)`
                                : '-'}
                            </td>
                            <td className="px-4 py-3">{formatCurrency(provider.loadedHourlyCost)}</td>
                            <td className="px-4 py-3">{formatCurrency(provider.weeklyCost)}</td>
                            <td className="px-4 py-3">{formatCurrency(provider.weeklyProfitTarget)}</td>
                            <td className="px-4 py-3">{formatCurrency(provider.weeklyIncomeToGenerate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <div className="text-right">
                <button
                  onClick={handleSaveTargetIncome}
                  className="px-6 py-3 bg-[color:var(--color-warn)] hover:brightness-95 text-[color:var(--color-text)] font-medium rounded-lg shadow-md disabled:opacity-70"
                  disabled={savingGoal}
                >
                  {savingGoal ? 'Saving...' : 'Save Budget Plan'}
                </button>
                {saveMessage.text && (
                  <div className={`mt-3 text-sm ${saveMessage.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                    {saveMessage.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="border-0 shadow-lg rounded-xl overflow-hidden">
            <div className="bg-[color:var(--color-accent)] text-[color:var(--color-text)] p-6">
              <h3 className="text-2xl">Profit Goal Progress</h3>
              <p className="opacity-90">
                {isLimitedCompany
                  ? 'Track progress on company profit toward your business goal.'
                  : 'Track progress on business surplus toward your goal.'}
              </p>
            </div>
            <div className="p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="border border-gray-200 shadow-md rounded-xl overflow-hidden">
                  <div className="pb-2 bg-primary-gradient p-4">
                    <h4 className="text-sm font-medium">Weekly Profit Goal</h4>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-[color:var(--color-text)]">{formatCurrency(targets.weekly)}</div>
                  </div>
                </div>
                <div className="border border-gray-200 shadow-md rounded-xl overflow-hidden">
                  <div className="pb-2 bg-primary-gradient p-4">
                    <h4 className="text-sm font-medium">Monthly Profit Goal</h4>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-[color:var(--color-text)]">{formatCurrency(targets.monthly)}</div>
                  </div>
                </div>
                <div className="border border-gray-200 shadow-md rounded-xl overflow-hidden">
                  <div className="pb-2 bg-primary-gradient p-4">
                    <h4 className="text-sm font-medium">Yearly Profit Goal</h4>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-[color:var(--color-text)]">{formatCurrency(targets.yearly)}</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 border border-gray-200 shadow-md rounded-xl overflow-hidden">
                <div className="pb-2 bg-primary-gradient p-4">
                  <h4 className="text-sm font-medium">This Month's Progress</h4>
                  <p className="text-xs text-[color:var(--color-text)]">Income minus expenses against your monthly profit goal.</p>
                </div>
                <div className="p-4">
                  <div className="flex justify-between">
                    <span className="font-semibold">You're {progress.percentage}% there!</span>
                    <span className="font-semibold">{formatCurrency(progress.actual)} of {formatCurrency(progress.target)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3.5 shadow-inner mt-2">
                    <div
                      className="bg-[color:var(--color-info)] h-3.5 rounded-full shadow-sm"
                      style={{ width: `${Math.min(100, progress.percentage)}%` }}
                    ></div>
                  </div>

                  <div className="mt-6">
                    <h5 className="font-medium mb-2">Weekly Breakdown</h5>
                    <div className="space-y-2">
                      {budgetTracking.map((week, index) => (
                        <div key={index} className="flex justify-between p-3 bg-gray-50 rounded-lg">
                          <span>{week.period}</span>
                          <div className="flex space-x-4">
                            <span className="text-[color:var(--color-text)]">Target: <span className="text-gray-800">{formatCurrency(week.target)}</span></span>
                            <span className="text-[color:var(--color-text)]">Actual: <span className={week.actual >= week.target ? 'text-green-600' : 'text-gray-800'}>{formatCurrency(week.actual)}</span></span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {(staffPerformanceProfiles.length > 0 || staffPerformanceRows.length > 0) && (
                <div className="mt-6 border border-gray-200 shadow-md rounded-xl overflow-hidden">
                  <div className="border-b border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-[color:var(--color-text)]">Staff Performance</h4>
                        <p className="mt-1 text-xs text-gray-600">
                          {staffPerformanceMeta.description} This estimates profit after labour only; products, overheads, VAT, and tax are not allocated here.
                        </p>
                      </div>
                      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                        {STAFF_PERFORMANCE_PERIOD_OPTIONS.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => setStaffPerformancePeriod(option.id)}
                            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                              staffPerformancePeriod === option.id
                                ? 'bg-[color:var(--color-primary)] text-white'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 space-y-4 bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs text-gray-500">Assigned sales in {staffPerformanceMeta.label.toLowerCase()}</p>
                        <p className="mt-1 text-2xl font-bold text-[color:var(--color-text)]">{assignedStaffSalesCount}</p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs text-gray-500">Top by revenue</p>
                        <p className="mt-1 text-lg font-bold text-[color:var(--color-text)]">
                          {topRevenueStaffMember ? topRevenueStaffMember.displayName : 'No assigned sales yet'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {topRevenueStaffMember ? formatCurrency(topRevenueStaffMember.revenue) : 'Assign staff names to sales to rank revenue.'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs text-gray-500">Top estimated profit after labour</p>
                        <p className="mt-1 text-lg font-bold text-[color:var(--color-text)]">
                          {topContributionStaffMember
                            ? topContributionStaffMember.displayName
                            : assignedStaffSalesCount > 0
                              ? 'Needs labour plan'
                              : 'No assigned sales yet'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {topContributionStaffMember && topContributionStaffMember.contributionAfterLabour !== null
                            ? formatCurrency(topContributionStaffMember.contributionAfterLabour)
                            : assignedStaffSalesCount > 0
                              ? 'Save staff hours and pay in Budget Plan first.'
                              : 'Assign staff names to sales to compare revenue against labour cost.'}
                        </p>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                          <tr>
                            <th className="px-4 py-3">Staff</th>
                            <th className="px-4 py-3">Sales</th>
                            <th className="px-4 py-3">Revenue</th>
                            <th className="px-4 py-3">Planned labour cost</th>
                            <th className="px-4 py-3">Revenue target</th>
                            <th className="px-4 py-3">Estimated profit after labour</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {staffPerformanceRows.map((row) => (
                            <tr key={row.id}>
                              <td className="px-4 py-3">
                                <div className="font-semibold text-[color:var(--color-text)]">{row.displayName}</div>
                                <div className="mt-1 text-xs text-gray-500">
                                  {row.isArchived
                                    ? 'Archived from sales history'
                                    : `Average sale ${formatCurrency(row.averageSaleValue)}`}
                                </div>
                              </td>
                              <td className="px-4 py-3">{row.saleCount}</td>
                              <td className="px-4 py-3">{formatCurrency(row.revenue)}</td>
                              <td className="px-4 py-3">
                                {row.plannedLabourCost === null ? 'Not set' : formatCurrency(row.plannedLabourCost)}
                              </td>
                              <td className="px-4 py-3">
                                {row.targetRevenue === null ? 'Not set' : formatCurrency(row.targetRevenue)}
                              </td>
                              <td className={`px-4 py-3 font-semibold ${
                                row.contributionAfterLabour === null
                                  ? 'text-gray-500'
                                  : row.contributionAfterLabour >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                              }`}>
                                {row.contributionAfterLabour === null
                                  ? 'Needs labour plan'
                                  : formatCurrency(row.contributionAfterLabour)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetSection;
